import type { Layout, LayoutItem } from "react-grid-layout/legacy"
import type { DashboardAction, DynamicPanelSpec } from "./chat-types"

export type ChatState = {
  layout: Layout
  dynamicPanels: Record<string, DynamicPanelSpec>
  panelFilters: Record<string, Record<string, string>>
  actionsLog: DashboardAction[]
}

const LOG_CAP = 50

function append(state: ChatState, action: DashboardAction): ChatState {
  const next = state.actionsLog.length >= LOG_CAP ? state.actionsLog.slice(1) : state.actionsLog
  return { ...state, actionsLog: [...next, action] }
}

function bottomOf(layout: readonly LayoutItem[]): number {
  return layout.reduce((m, l) => Math.max(m, l.y + l.h), 0)
}

export function applyDashboardAction(state: ChatState, action: DashboardAction): ChatState {
  switch (action.action_type) {
    case "add_panel": {
      const payload = action.payload as {
        panel_type: string
        x?: number
        y?: number
        w?: number
        h?: number
      }
      if (state.layout.some((l) => l.i === payload.panel_type)) return append(state, action)
      const entry: LayoutItem = {
        i: payload.panel_type,
        x: payload.x ?? 0,
        y: payload.y ?? bottomOf(state.layout),
        w: payload.w ?? 6,
        h: payload.h ?? 6,
      }
      return append({ ...state, layout: [...state.layout, entry] }, action)
    }
    case "remove_panel": {
      const { panel_id } = action.payload as { panel_id: string }
      const layout = state.layout.filter((l) => l.i !== panel_id)
      const dyn = { ...state.dynamicPanels }
      delete dyn[panel_id]
      return append({ ...state, layout, dynamicPanels: dyn }, action)
    }
    case "move_panel": {
      const p = action.payload as {
        panel_id: string
        x: number
        y: number
        w: number
        h: number
      }
      const layout = state.layout.map((l) =>
        l.i === p.panel_id ? { ...l, x: p.x, y: p.y, w: p.w, h: p.h } : l,
      )
      return append({ ...state, layout }, action)
    }
    case "set_panel_filter": {
      const p = action.payload as {
        panel_id: string
        filter_key: string
        filter_value: string
      }
      const current = state.panelFilters[p.panel_id] ?? {}
      return append(
        {
          ...state,
          panelFilters: {
            ...state.panelFilters,
            [p.panel_id]: { ...current, [p.filter_key]: p.filter_value },
          },
        },
        action,
      )
    }
    case "create_dynamic_panel": {
      const spec = action.payload as unknown as DynamicPanelSpec
      const entry: LayoutItem = {
        i: spec.id,
        x: 0,
        y: bottomOf(state.layout),
        w: 6,
        h: 6,
      }
      return append(
        {
          ...state,
          dynamicPanels: { ...state.dynamicPanels, [spec.id]: spec },
          layout: [...state.layout, entry],
        },
        action,
      )
    }
    case "download_file": {
      const p = action.payload as { filename: string; mime: string; content: string }
      if (typeof window !== "undefined" && typeof document !== "undefined") {
        try {
          const blob = new Blob([p.content], { type: p.mime })
          const url = URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = url
          a.download = p.filename
          // Some browsers require the anchor to be in the DOM before click()
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          // Revoke after a short delay so the download starts
          setTimeout(() => URL.revokeObjectURL(url), 1000)
        } catch {
          // Non-fatal — just log the action and continue
        }
      }
      return append(state, action)
    }
    case "highlight_region":
    case "save_view":
    default:
      return append(state, action)
  }
}

export function inverseAction(
  state: ChatState,
  action: DashboardAction,
): DashboardAction | null {
  switch (action.action_type) {
    case "add_panel": {
      const { panel_type } = action.payload as { panel_type: string }
      return { action_type: "remove_panel", payload: { panel_id: panel_type } }
    }
    case "remove_panel": {
      const { panel_id } = action.payload as { panel_id: string }
      const prev = state.layout.find((l) => l.i === panel_id)
      if (!prev) return null
      return {
        action_type: "add_panel",
        payload: { panel_type: panel_id, x: prev.x, y: prev.y, w: prev.w, h: prev.h },
      }
    }
    case "move_panel": {
      const p = action.payload as { panel_id: string; x: number; y: number; w: number; h: number }
      const prev = state.layout.find((l) => l.i === p.panel_id)
      if (!prev) return null
      return {
        action_type: "move_panel",
        payload: { panel_id: p.panel_id, x: prev.x, y: prev.y, w: prev.w, h: prev.h },
      }
    }
    case "create_dynamic_panel": {
      const spec = action.payload as { id: string }
      return { action_type: "remove_panel", payload: { panel_id: spec.id } }
    }
    default:
      return null
  }
}
